import type { ColumnDef } from "@tanstack/react-table";
import type { TFunction } from "i18next";
import Text from "@/components/ui/text";
import { formatFullDate } from "@/utils/datetime";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { DeltaValueCell, ImpactCell } from "./cells";
import {
  deltaColumnId,
  deltaImpactColumnId,
  deltaHeader,
  deltaImpactHeader,
  type CompareTranslationKey
} from "./columnIds";
import { resolveImpact, spansTwoCurrencies } from "./impactResolution";
import type { CompareMetricKey, RowComparison } from "./types";

interface RowWithId {
  id: string;
}

interface DeltaColumnArgsBase {
  /** The response key for this metric, e.g. `arr` or `arrLocal`. */
  metricKey: CompareMetricKey;
  /** The stable column id the Δ pair hangs off, normally the metric column's id. */
  columnId: string;
  /** The metric's own display label, e.g. `ARR`. */
  metricLabel: string;
  /** The rendered timeframe, e.g. `YTD` or `Since 12 Nov 25`. */
  timeframe: string;
  /**
   * The period start the server actually resolved, shown on the header. The
   * client mirrors the backend's period math to label the picker, so this is
   * what makes any drift between the two visible instead of silent.
   */
  periodStart: string | null;
  /**
   * The day the comparison window closes — today, since the endpoint measures
   * against live values. Bounds the New label: a contract whose term opens
   * after this has not begun inside the window, however positive its figure.
   */
  periodEnd: string | null;
  byRowId: Map<string, RowComparison>;
  /**
   * Rows the endpoint reported it could not resolve — a company deleted
   * between the page load and the call, say.
   *
   * Separate from mere absence from `byRowId`: an absent row and an
   * unresolvable one both have no comparison, but only the second is a fact we
   * can state. Without it the cell renders bare and a deleted row reads as one
   * that did not move, which is the trap the endpoint added the field to avoid.
   */
  notFoundRowIds?: ReadonlySet<string>;
  isTermsCurrency: boolean;
  /** Where this grain's Compare-to copy lives, e.g. `contracts.compareTo`. */
  translationKey: CompareTranslationKey;
  /**
   * Whether to emit the Impact column alongside the Δ. The spec pairs one
   * Impact column with each metric, not with each currency basis — the
   * terms-currency Δ describes the same movement, so a second label beside it
   * would say the same thing twice.
   */
  includeImpact: boolean;
  t: TFunction;
}

/**
 * Whether this grain can derive the New label, and the row evidence that makes
 * it possible — expressed as a union so the two cannot be set independently.
 *
 * `readRowStart` is the only evidence separating "this row is new" from "the
 * snapshot table does not reach back that far", and the two are
 * indistinguishable in the response. Omitting it under `allowNew: true` does
 * not fail: the optional call yields null, `startedWithinPeriod` fails closed,
 * and the grain silently stops labelling anything New. Requiring it in the type
 * is what turns that into a compile error.
 *
 * `allowNew: false` takes no reader, because no amount of row evidence can
 * produce the label then — a company row carries no evidence of a previous
 * *figure*, so New is withheld there: the label is specified for Contracts
 * and Services only.
 */
export type DeltaColumnArgs<TRow extends RowWithId> =
  DeltaColumnArgsBase & NewLabelPolicy<TRow>;

export type NewLabelPolicy<TRow extends RowWithId> =
  | {
      allowNew: true;
      readRowStart: (row: TRow) => string | null;
      /** Set where the design labels a row that has not started yet New. */
      allowFutureStart?: boolean;
    }
  | { allowNew: false; readRowStart?: undefined; allowFutureStart?: undefined };

/**
 * The Δ and Δ Impact column pair for one metric.
 *
 * Both are client-computed from a companion endpoint, so neither can be sorted:
 * the table hands sorting to the API, which has no field to sort these on. A
 * client `sortingFn` would be inert under `manualSorting` and a sortable header
 * would send the server a field it rejects.
 */
export const buildDeltaColumns = <TRow extends RowWithId>({
  metricKey,
  columnId,
  metricLabel,
  timeframe,
  periodStart,
  periodEnd,
  byRowId,
  notFoundRowIds,
  isTermsCurrency,
  translationKey,
  includeImpact,
  allowNew,
  readRowStart,
  allowFutureStart,
  t
}: DeltaColumnArgs<TRow>): ColumnDef<TRow, unknown>[] => {
  const readComparison = (row: TRow) => byRowId.get(row.id);

  const headerWithPeriod = (label: string) =>
    periodStart
      ? () => (
          <Tooltip>
            <TooltipTrigger asChild>
              {/* A button rather than a span: the trigger has to be reachable
                  by keyboard, or the comparison date is available only on
                  hover. `cursor-default` keeps it reading as a column header
                  rather than an action. */}
              <button type="button" className="cursor-default text-left">
                {label}
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <Text variant="body">
                {t(`${translationKey}.comparedSince`, {
                  date: formatFullDate(periodStart)
                })}
              </Text>
            </TooltipContent>
          </Tooltip>
        )
      : label;

  const deltaLabel = deltaHeader(metricLabel, timeframe);
  const impactLabel = deltaImpactHeader(
    metricLabel,
    timeframe,
    t,
    translationKey
  );

  // A button, not a span: Text is not a forwardRef component so Radix needs a
  // wrapper to attach its positioning ref to, and that wrapper has to be
  // focusable — the reason a cell is empty is otherwise reachable only by
  // hovering it. Shared by both columns so an unavailable row explains itself
  // the same way in each.
  const withdrawn = (reason: string) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" className="cursor-default" aria-label={reason}>
          <Text as="span" variant="bodyTertiary">
            —
          </Text>
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <Text variant="body">{reason}</Text>
      </TooltipContent>
    </Tooltip>
  );

  const deltaColumn: ColumnDef<TRow, unknown> = {
    id: deltaColumnId(columnId),
    header: headerWithPeriod(deltaLabel),
    // `header` becomes a render function once there is a period to show, and
    // the column picker derives its label with `String(column.header)` — which
    // on a function yields its source. The readable label has to survive
    // somewhere that is always a string.
    meta: { label: deltaLabel },
    enableSorting: false,
    size: 160,
    cell: (info) => {
      const comparison = readComparison(info.row.original);
      if (!comparison) {
        // An id the endpoint reported as unresolvable is a statable fact; a row
        // simply missing from the response is not, and stays blank.
        return notFoundRowIds?.has(info.row.original.id)
          ? withdrawn(t(`${translationKey}.rowNotFound`))
          : null;
      }
      if (!comparison.existed) {
        // Every metric on the row reports a null delta for this one shared
        // reason, and an unexplained blank cell reads as "no change" — the one
        // thing it does not mean. Say which date nothing was recorded at.
        return withdrawn(
          t(`${translationKey}.noPriorRecord`, {
            date: periodStart ? formatFullDate(periodStart) : timeframe
          })
        );
      }
      if (isTermsCurrency && spansTwoCurrencies(comparison)) {
        return withdrawn(
          t(`${translationKey}.currencyChanged`, {
            previous: comparison.previousCurrency,
            current: comparison.currency
          })
        );
      }
      return (
        <DeltaValueCell
          metric={comparison.metrics[metricKey]}
          // A grain that reports no currency at all (companies) is the same
          // "nothing to denominate this in" case as a row whose currency is
          // null, and the cell already renders that unadorned.
          currency={comparison.currency ?? null}
          isTermsCurrency={isTermsCurrency}
        />
      );
    }
  };

  const impactColumn: ColumnDef<TRow, unknown> = {
    id: deltaImpactColumnId(columnId),
    header: headerWithPeriod(impactLabel),
    meta: { label: impactLabel },
    enableSorting: false,
    size: 190,
    cell: (info) => {
      const comparison = readComparison(info.row.original);
      const metric = comparison?.metrics[metricKey];
      if (!comparison) {
        // Same treatment as the Δ cell: an id the endpoint reported as
        // unresolvable is a statable fact, and leaving this blank beside a Δ
        // cell that explains itself reads as "no impact" rather than "we could
        // not look this row up". This states absence; it does not assert a
        // label, which is what would be wrong for a row that was never found.
        return notFoundRowIds?.has(info.row.original.id)
          ? withdrawn(t(`${translationKey}.rowNotFound`))
          : null;
      }
      if (!metric) return null;
      // Where the grain cannot derive New, the server's label stands as-is.
      // Routing through resolveImpact with a null start would reach the same
      // answer, but only by accident of the fail-closed check — saying so here
      // keeps it true if that check ever loosens.
      const impact = allowNew
        ? resolveImpact(metric, comparison, {
            rowStart: readRowStart?.(info.row.original) ?? null,
            periodStart,
            periodEnd,
            allowFutureStart
          })
        : metric.impact;
      // `allowNew: false` is a statement about what may appear on this table,
      // not merely about what the client derives, so the server's own label is
      // filtered too. Today the company endpoint hardcodes its equivalent gate
      // and documents that `new` is never returned, so this only fires if that
      // contract is broken — blank rather than a substitute label, because
      // `noChange` would assert the row did not move, which is the one thing a
      // `new` label rules out. The Δ cell still shows the movement.
      if (!allowNew && impact === "new") return null;
      // A brand-new contract has no prior figure, so its delta is null while
      // its impact is still knowable — New is the one label that survives a
      // null delta. Every other label is a statement about movement, and the
      // API answers `noChange` whenever it had nothing to compare, so showing
      // it here would assert a contract did not move when nobody knows.
      if (impact !== "new" && metric.delta == null) return null;
      return (
        <ImpactCell impact={impact} t={t} translationKey={translationKey} />
      );
    }
  };

  return includeImpact ? [deltaColumn, impactColumn] : [deltaColumn];
};
